import React, { useState, useEffect, CSSProperties } from "react";
import config from "../../../config";
import "./CustomizeBottle.css";
import {
  BottleColor,
  CustomBottleAPIStyles,
  sampleBottleResponse,
} from "../../../Interfaces/Bottles";
import AddToCartButton from "../AddToCartButton";
import { FundraiserParticipantShop } from "../../../Interfaces/FundraiserRole";
import {
  CartItem,
  createEmptyCartItemWithCustom,
  generateUniqueId,
} from "../../../Interfaces/Types";
import { useNavigate } from "react-router/dist";
import { useCart } from "../../../contexts/Cart/CartContext";
import EmptyParticipantShop from "../MissingParticipant";
import { useSearchParams } from "react-router-dom";
import apiClient from "../../../services/apiClient";

const BASE_API = config.API_BASE_URL;

const BottleCustomizationPage = () => {
  const [bottleData, setBottleData] = useState<CustomBottleAPIStyles[]>([]);
  const [selectedStyle, setSelectedStyle] =
    useState<CustomBottleAPIStyles | null>(null);
  const [selectedColor, setSelectedColor] = useState<BottleColor | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [text, setText] = useState<string>("");
  const [bottleItem, setBottleItem] = useState<CartItem>(
    createEmptyCartItemWithCustom()
  );
  const [error, setError] = useState("");
  const [bottleDesign, setBottleDesign] = useState<string>("");
  const navigate = useNavigate();
  const { state } = useCart();

  // Font state: Store the selected font and available fonts
  const [font, setFont] = useState<string>("Dancing Script"); // Default font
  const availableFonts = [
    "Dancing Script",
    "Storybook",
    "Witches and Wizards",
    "Varsity Team",
    "Advera",
  ]; // Add more font families here based on your CSS @font-face rules
  const [searchParams] = useSearchParams();
  const imageLocation = searchParams.get("imageLocation");
  const fundraiserID = searchParams.get("fundraiserID");

  const isOrgBottle = () => {
    const keywords = ["Organizatioons", "Organizations"]; //because of Jeffs poor spelling :)
    if (imageLocation) {
      return keywords.some((keyword) => imageLocation.includes(keyword));
    }
    return false;
  };

  useEffect(() => {
    const apiPath = isOrgBottle()
      ? `fundraiser/${fundraiserID}/custom_bottles/org/options`
      : "fundraiser/custom_bottles/non_org/options";

    apiClient
      .get(`${BASE_API}${apiPath}`)
      .then((response) => {
        setBottleData(response.data);
        setSelectedStyle(response.data.length ? response.data[0] : null);
        setSelectedColor(
          response.data.length ? response.data[0].color_options[0] : null
        );
      })
      .catch((error) => {
        console.error("Failed to fetch bottle customization data:", error);
      });

    setBottleDesign(
      `${config.BACKEND_BASE_IMAGE_LOCATION}${imageLocation}` || ""
    ); // Get the proper logo either from url?
  }, []);

  useEffect(() => {
    if (selectedColor && selectedStyle) {
      let hasError = false;

      // Check for SKU error
      if (!selectedColor.sku) {
        console.error("SKU is missing for the selected color:", selectedColor);
        setError(
          "An error has occurred. Please refresh the page and try again."
        );
        hasError = true;
      }

      // Check for price error
      if (selectedStyle.cost == null || selectedStyle.cost === 0) {
        console.error(
          "Price is missing or invalid for the selected style:",
          selectedStyle
        );
        setError(
          "An error has occurred. Please refresh the page and try again."
        );
        hasError = true;
      }

      // If there are no errors, update the bottle item
      if (!hasError) {
        setError(""); // Clear error message if everything is valid
        setBottleItem((prevItem) => ({
          ...prevItem,
          sku: selectedColor.sku,
          name: `${selectedStyle.style || "Default Style"} ${
            selectedColor.color || "Default Color"
          }`,
          price: selectedStyle.cost, // Price should be valid here
          quantity: quantity,
          image: `${config.BACKEND_BASE_IMAGE_LOCATION}${selectedColor.image_url}`,
          customization: {
            ...prevItem.customization,
            design: bottleDesign, // Assuming bottle_design is empty
            bottle_text: text || "No Text",
            font_style: font,
          },
        }));
      }
    } else {
      console.error("Missing selected color or style:", {
        selectedColor,
        selectedStyle,
      });
      setError("An error has occurred. Please refresh the page and try again.");
    }
  }, [selectedColor, selectedStyle, quantity, text, font]);

  const handleStyleSelect = (style: CustomBottleAPIStyles) => {
    setSelectedStyle(style);
    setSelectedColor(style.color_options[0]);
  };

  const handleShopOtherProducts = () => {
    const participant_id = state.fundraiser_participant?.participant_code;
    navigate(`/participant-shop/part-id/${participant_id}`);
  };

  const handleColorSelect = (color: BottleColor) => setSelectedColor(color);
  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setQuantity(parseInt(event.target.value, 10) || 1);
  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setText(event.target.value);
  const handleFontChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setFont(event.target.value);

  const calculateFontSize = (text: string, baseSize: number): number => {
    const base_modifier = window.innerWidth > 768 ? 40 : 25; // different base modifiers for desktop/mobile
    const small_length = 6;
    if (text.length < small_length) {
      return baseSize * (base_modifier / small_length); // make a base size otherwise it stretches out too much
    }
    return baseSize * (base_modifier / text.length);
  };

  const textStyle: CSSProperties = selectedStyle?.textPlacement
    ? {
        top: `${selectedStyle.textPlacement.top}`,
        left: `${selectedStyle.textPlacement.left}`,
        fontFamily: font,
        fontSize: `${calculateFontSize(
          text,
          selectedStyle.textPlacement.fontSize
        )}%`,
        position: "absolute",
        display: "flex", // Flexbox for centering
        justifyContent: "center", // Horizontal centering
        alignItems: "center", // Vertical centering
        transform:
          selectedStyle.textOrientation === "vertical"
            ? "rotate(90deg)"
            : "none",
        transformOrigin: "center center",
        width: `${selectedStyle.textPlacement.width}`,
        height: `${selectedStyle.textPlacement.height}`,
        textAlign: "center",
        whiteSpace: "normal",
        lineHeight: "1",
        overflow: "hidden",
      }
    : {};

  if (!bottleData.length) {
    return <p>Loading...</p>;
  }

  if (!state.fundraiser_participant) {
    return <EmptyParticipantShop />;
  }
  return (
    <div className="storefront-bottle-customization-product-page">
      <div className="storefront-bottle-customization-image-and-details">
        {/* Bottle Image Section */}
        <div className="storefront-bottle-customization-image-gallery">
          {selectedColor && (
            <div className="storefront-bottle-customization-image-container">
              {/* Main Bottle Image */}
              <img
                src={`${config.BACKEND_BASE_IMAGE_LOCATION}${selectedColor.image_url}`}
                alt={`${selectedColor.color} bottle`}
                className="storefront-bottle-customization-bottle-image"
              />

              {/* Dynamic Text Overlay */}
              {text && selectedStyle && (
                <div
                  className={`storefront-bottle-customization-text-overlay ${
                    selectedStyle.textOrientation === "vertical"
                      ? "vertical-text"
                      : ""
                  }`}
                  style={textStyle}
                >
                  {text}
                </div>
              )}

              {/* Predefined Logo Overlay (Position Controlled via API) */}
              {selectedStyle?.logoPlacement && (
                <img
                  src={bottleDesign}
                  alt="Custom Logo"
                  className="storefront-bottle-customization-logo-overlay"
                  style={{
                    top: `${selectedStyle.logoPlacement.top}`,
                    left: `${selectedStyle.logoPlacement.left}`,
                    width: `${selectedStyle.logoPlacement.fontSize}%`,
                    height: `${selectedStyle.logoPlacement.fontSize}%`,
                    position: "absolute",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              )}
            </div>
          )}

          {/* Disclaimer Text Below the Image */}
          <div className="storefront-bottle-customization-disclaimer">
            <p style={{ fontSize: "12px", color: "#888", marginTop: "10px" }}>
              * Please note that the final product may vary slightly from the
              images shown depending on screen settings and design adjustments.
            </p>
          </div>
        </div>

        {/* Product Details Section */}
        <div className="storefront-bottle-customization-details">
          <h1>Bottle Customization</h1>

          <label>Choose a style:</label>
          {/* Style Selection */}
          <div className="storefront-bottle-customization-style-selection">
            {bottleData.map(
              (style, index) =>
                style.color_options.length > 0 && (
                  <button
                    key={index}
                    className={`storefront-bottle-customization-style-option ${
                      selectedStyle?.style === style.style ? "selected" : ""
                    }`}
                    onClick={() => handleStyleSelect(style)}
                  >
                    {style.style}
                  </button>
                )
            )}
          </div>

          {/* Price and Options */}
          {selectedStyle && (
            <>
              {/* Color Options */}
              <div className="storefront-bottle-customization-color-selection">
                <p>Available in {selectedStyle.color_options.length} colors</p>
                <div className="storefront-bottle-customization-color-options">
                  {selectedStyle.color_options.map((color, index) => (
                    <div
                      className="storefront-bottle-customization-color-option-container"
                      key={index}
                    >
                      <button
                        className={`storefront-bottle-customization-color-option ${
                          selectedColor?.color === color.color ? "selected" : ""
                        }`}
                        style={{ backgroundColor: color.hex_code }}
                        onClick={() => handleColorSelect(color)}
                      />
                      <div className="storefront-bottle-customization-color-option-name">
                        {color.color} {/* Text is below the swatch */}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Text Customization */}
              <div className="storefront-bottle-customization-text-customization">
                <label htmlFor="text">Choose Text and Font (Optional):</label>
                <input
                  type="text"
                  id="text"
                  value={text}
                  maxLength={20}
                  onChange={handleTextChange}
                  autoComplete="off"
                  placeholder="Enter your text"
                  className="storefront-bottle-customization-input-forms"
                />
                <select
                  value={font}
                  onChange={handleFontChange}
                  className="storefront-bottle-customization-input-forms"
                >
                  {availableFonts.map((fontOption, index) => (
                    <option key={index} value={fontOption}>
                      {fontOption}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantity Selector */}
              <div className="storefront-bottle-customization-quantity-selector">
                <label htmlFor="quantity">Quantity:</label>
                <input
                  type="number"
                  id="quantity"
                  value={quantity}
                  min={1}
                  onChange={handleQuantityChange}
                  className="storefront-bottle-customization-input-forms"
                />
              </div>

              <p className="storefront-bottle-customization-price">
                ${selectedStyle.cost.toFixed(2)}
              </p>

              {/* Add to Cart Button */}
              <div className="storefront-bottle-customization-action-buttons">
                <AddToCartButton
                  item={bottleItem}
                  participant={state.fundraiser_participant}
                  disabled={!!error}
                  className="store-front-customize-bottles-button-add-to-cart"
                />
                {/* "Shop Other Products" Button */}
                <button
                  className="shop-other-products-button"
                  onClick={handleShopOtherProducts}
                >
                  Continue Shopping
                </button>
              </div>
              <div>
                {error && (
                  <div style={{ color: "red", fontWeight: "bold" }}>
                    {error}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BottleCustomizationPage;
